// The recruiter app's display languages. i18next-free and React-free: the app
// entry reads the cached language through here before any app chunk loads.
import type { UiLanguage } from 'api/generated/types/uiLanguage';

import { localeDependencies } from '../i18nLocaleLoader';

export type RecruiterLanguage = UiLanguage;

/** Every language a user can pick, in picker order. English is the source. */
export const RECRUITER_LANGUAGES = [
  'en',
  'fr',
  'es',
  'es-ES',
  'de',
  'nl',
  'nl-BE',
] as const satisfies readonly RecruiterLanguage[];

// Fails to compile when the backend enum gains a language this list lacks.
type MissingLanguage = Exclude<
  RecruiterLanguage,
  (typeof RECRUITER_LANGUAGES)[number]
>;
const assertNoMissingLanguage: MissingLanguage extends never ? true : never =
  true;
void assertNoMissingLanguage;

export const DEFAULT_RECRUITER_LANGUAGE: RecruiterLanguage = 'en';

export const isRecruiterLanguage = (
  value: string | null | undefined
): value is RecruiterLanguage =>
  typeof value === 'string' &&
  (RECRUITER_LANGUAGES as readonly string[]).includes(value);

/**
 * A stored or server-sent language, or English. There is no browser
 * auto-detection: a user who never picked a language sees English.
 */
export const normalizeRecruiterLanguage = (
  value: string | null | undefined
): RecruiterLanguage =>
  isRecruiterLanguage(value) ? value : DEFAULT_RECRUITER_LANGUAGE;

/**
 * The language the recruiter app shows: the user's own choice, else the
 * organization default, else English. Mirrors the server's
 * `effective_ui_language`, for the picker to cache after a save.
 */
export const resolveRecruiterLanguage = (
  userChoice: RecruiterLanguage | null | undefined,
  orgDefault: RecruiterLanguage | null | undefined
): RecruiterLanguage => userChoice ?? orgDefault ?? DEFAULT_RECRUITER_LANGUAGE;

/**
 * The languages whose bundles must be registered to show `language`, in
 * fallback-first order. Regional languages (es-ES, nl-BE) ship overrides only,
 * so they always need their base language too;
 * the chain is the candidate loader's, which mirrors fallbackLng in i18n.ts.
 */
export const bundleLanguagesFor = (
  language: RecruiterLanguage
): RecruiterLanguage[] => {
  if (language === 'en') return [];
  const dependency: string | undefined = localeDependencies[language];
  return isRecruiterLanguage(dependency) ? [dependency, language] : [language];
};

/** Intl locale used when the browser's own locale is a different language. */
export const DEFAULT_INTL_LOCALE: Record<RecruiterLanguage, string> = {
  en: 'en-US',
  fr: 'fr-FR',
  es: 'es-419',
  'es-ES': 'es-ES',
  de: 'de-DE',
  nl: 'nl-NL',
  'nl-BE': 'nl-BE',
};

/** dayjs locale names, which Mantine's date components take as `locale`. */
export const DAYJS_LOCALE: Record<RecruiterLanguage, string> = {
  en: 'en',
  fr: 'fr',
  es: 'es-mx',
  'es-ES': 'es',
  de: 'de',
  nl: 'nl',
  'nl-BE': 'nl-be',
};

export interface RecruiterLanguageOption {
  code: RecruiterLanguage;
  /** Endonym: each language is named in itself, so it is never translated. */
  label: string;
  emoji: string;
  countryCode: string;
}

export const RECRUITER_LANGUAGE_OPTIONS: readonly RecruiterLanguageOption[] = [
  // i18n-ok: endonym
  { code: 'en', label: 'English', emoji: '🇺🇸', countryCode: 'US' },
  // i18n-ok: endonym
  { code: 'fr', label: 'Français', emoji: '🇫🇷', countryCode: 'FR' },
  {
    code: 'es',
    // i18n-ok: endonym
    label: 'Español (Latinoamérica)',
    emoji: '🇲🇽',
    countryCode: 'MX',
  },
  {
    code: 'es-ES',
    // i18n-ok: endonym
    label: 'Español (España)',
    emoji: '🇪🇸',
    countryCode: 'ES',
  },
  // i18n-ok: endonym
  { code: 'de', label: 'Deutsch', emoji: '🇩🇪', countryCode: 'DE' },
  // i18n-ok: endonym
  { code: 'nl', label: 'Nederlands', emoji: '🇳🇱', countryCode: 'NL' },
  {
    code: 'nl-BE',
    // i18n-ok: endonym
    label: 'Nederlands (België)',
    emoji: '🇧🇪',
    countryCode: 'BE',
  },
];

/**
 * Every recruiter namespace. Each one has a module under ./namespaces that
 * registers its English bundle (except `errors`, whose English is the server's
 * error detail), and a JSON file per language under locales/<lang>/recruiter/.
 */
export const RECRUITER_NAMESPACES = [
  'core',
  'account',
  'adminAts',
  'adminCompliance',
  'adminData',
  'adminOrg',
  'alerts',
  'analytics',
  'atsConfig',
  'billing',
  'candidates',
  'devtools',
  'domain',
  'errors',
  'external-agent',
  'internalTools',
  'jobEditor',
  'jobEditorFlow',
  'jobSettings',
  'jobSettingsControls',
  'jobs',
  'login',
  'notes',
  'scriptTesting',
  'shell',
  'shellPages',
  'sourcing',
  'templates',
  'transcript',
] as const;

export type RecruiterNamespace = (typeof RECRUITER_NAMESPACES)[number];

/**
 * Loaded with the coded API error translator (index.ts) rather than with the
 * startup bundle: only apiErrorText.ts reads it.
 */
export const RECRUITER_ERROR_NAMESPACES = [
  'errors',
] as const satisfies readonly RecruiterNamespace[];

/**
 * Staff-only (dev and support tools): loaded for staff sessions and the
 * internal app (index.ts), not with every customer's startup bundle.
 */
export const RECRUITER_STAFF_NAMESPACES = [
  'devtools',
  'internalTools',
] as const satisfies readonly RecruiterNamespace[];

/** Administrative namespaces load only after their components register. */
export const RECRUITER_ADMIN_NAMESPACES = [
  'adminAts',
  'adminCompliance',
  'adminData',
  'adminOrg',
] as const satisfies readonly RecruiterNamespace[];
